import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  afterNextRender,
  inject,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MapClickContext } from '../../utils/map-click-context.models';
import { MenuPosition } from '../../utils/map-request-menu.util';

/**
 * Context menu shown on a right-click of the satellite-images map. It offers one action:
 * create a satellite-image processing request for the clicked point.
 *
 * Keyboard: focus moves to the first item when the menu opens, Arrow Up/Down, Home and End move
 * between items, Escape or an outside click closes it, and focus returns to the previously focused
 * element on close. Outside-click and Escape listeners exist only while the menu is shown.
 */
@Component({
  selector: 'app-map-request-context-menu',
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './map-request-context-menu.component.html',
  styleUrl: './map-request-context-menu.component.scss',
})
export class MapRequestContextMenuComponent implements OnChanges, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  @Input() show = false;
  /** Viewport position (fixed) of the menu top-left corner. */
  @Input() position: MenuPosition = { top: 0, left: 0 };
  @Input() contextData: MapClickContext | null = null;

  @Output() createRequest = new EventEmitter<MapClickContext>();
  @Output() close = new EventEmitter<void>();

  private previousFocus: HTMLElement | null = null;
  private listening = false;

  private readonly onDocumentClick = (event: Event): void => {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.close.emit();
    }
  };

  private readonly onDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.close.emit();
    }
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['show']) {
      return;
    }
    if (this.show) {
      this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.listen();
      afterNextRender(() => this.items()[0]?.focus(), { injector: this.injector });
    } else {
      this.unlisten();
      this.restoreFocus();
    }
  }

  ngOnDestroy(): void {
    this.unlisten();
  }

  onCreateRequest(): void {
    const context = this.contextData;
    // Close first: closing hides the menu, and the action starts its own work after this.
    this.close.emit();
    if (context) {
      this.createRequest.emit(context);
    }
  }

  onMenuKeydown(event: KeyboardEvent): void {
    const items = this.items();
    if (!items.length) {
      return;
    }
    const current = items.indexOf(document.activeElement as HTMLElement);
    let next: number;
    switch (event.key) {
      case 'ArrowDown':
        next = (current + 1) % items.length;
        break;
      case 'ArrowUp':
        next = (current - 1 + items.length) % items.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    items[next].focus();
  }

  private items(): HTMLElement[] {
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('[role="menuitem"]'));
  }

  private listen(): void {
    if (this.listening) {
      return;
    }
    this.listening = true;
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
  }

  private unlisten(): void {
    if (!this.listening) {
      return;
    }
    this.listening = false;
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  }

  private restoreFocus(): void {
    const target = this.previousFocus;
    this.previousFocus = null;
    if (target?.isConnected) {
      target.focus();
    }
  }
}
